import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMainSectionParameter } from 'components/Dashboard/helpers/useMainSectionParameter';
import SaveUpdatesToFolder from 'components/Dashboard/SubNav/ui/SaveUpdatesToFolder';
import { store } from 'components/Store';
import { dataHubUsers } from 'components/Dashboard/SidebarElements/parliamentarianUsers';
import { useContext, useEffect, useRef, useState } from 'react';
import RateMultipleUpdatesButton from 'components/Dashboard/SubNav/RateMultipleUpdatesButton';
import RemoveInteractionsModal from 'components/Dashboard/Influence/CrmInteractions/RemoveInteractionsModal';
import AddTagsToInteractionsModal from 'components/Dashboard/Influence/CrmInteractions/AddTagsToInteractionsModal';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';

const MainListActionsBar = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { selectedUpdates, activePlan } = state;

  const { enableReports } = activePlan ?? {};
  const dataHubUser = dataHubUsers(activePlan);
  const { setIsOpenReportPopup, setIsOpenLoggedOut, hasReportableUpdates } = props ?? {};
  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [isOpenAddTagsModal, setIsOpenAddTagsModal] = useState(false);
  const [isOpenMoreOptions, setIsOpenMoreOptions] = useState(false);
  const moreOptionsRef = useRef(null);
  useOutsideElement(moreOptionsRef, () => setIsOpenMoreOptions(false));

  // Below this width, action buttons collapse to icon-only (reusing CrmContactsListBar.jsx's own
  // CSS, e.g. contacts-list-bar-action-container-btn) and act on a single tap - no reveal-label
  // step, since these buttons don't commit to anything destructive on their own (a tap either opens
  // a modal/dropdown the user can still back out of, or is itself a low-stakes toggle).
  const onlyIconOnMobile = useIsMobile(677);

  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);
  const [searchValue, setSearchValue] = useState(query.get('search') ? decodeURIComponent(query.get('search')) : '');

  useEffect(() => {
    setSearchValue(query.get('search') ? decodeURIComponent(query.get('search')) : '');
  }, [location.search]);

  const setSearchParam = (value) => {
    const params = new URLSearchParams(location.search);
    if (value === '') {
      params.delete('search');
    } else {
      params.set('search', encodeURIComponent(value));
    }
    navigate({ search: `?${params.toString()}` });
  };

  const allInteractions =
    selectedUpdates?.length > 0 && selectedUpdates.every((item) => item?.type === 'CrmInteraction');
  // The search pill writes to this page's own ?search= param, which only the Interactions list page
  // actually reads back out to filter its query (see InfluenceCrmSubNav.jsx's searchBarSubNavCondition)
  // - on Engagement Stats' drill-down panel (also an all-interactions selection) it'd be a dead input.
  const interactionsListPage = location.pathname === '/people/interactions';

  const timeoutRef = useRef(null);

  const { inboxSection, dashboardSection } = useMainSectionParameter();
  const { resetSelectedUpdatesState } = useMainActionsBarFunctions();
  const { isAuthenticated } = useAuth0();

  useEffect(() => {
    if (selectedUpdates?.length > 0) {
      setShouldRender(true);
      timeoutRef.current = setTimeout(() => setIsVisible(true), 10);
    } else if (shouldRender) {
      setIsVisible(false);
      timeoutRef.current = setTimeout(() => setShouldRender(false), 250);
    }
    return () => clearTimeout(timeoutRef.current);
  }, [selectedUpdates, shouldRender]);

  const embargoedUpdates = selectedUpdates?.some((item) => item?.embargoed);

  // Dropped entirely on mobile rather than collapsed to icon-only like the others - there isn't
  // room for every action there, and adding to a report is the one least likely to be needed
  // in-the-moment from a phone.
  const addToReportButton = hasReportableUpdates && !onlyIconOnMobile && (
    <button
      className={`contacts-list-bar-action-container-btn ${
        enableReports && !embargoedUpdates && !dataHubUser ? '' : 'light-50-text'
      }`}
      onClick={() => {
        if (!embargoedUpdates && !dataHubUser) {
          if (enableReports && isAuthenticated) {
            setIsOpenReportPopup(true);
          } else {
            setIsOpenLoggedOut(true);
          }
        }
      }}
      data-tooltip-content={
        embargoedUpdates
          ? 'One or more updates selected are currently under embargo. You’ll be able to add them to reports once the embargo has been lifted.'
          : ''
      }
      data-tooltip-id='general-tooltip'
      data-tooltip-class-name='feed-tooltip'
      data-tooltip-place='bottom'
    >
      <span className='icon-document' />
      {onlyIconOnMobile ? '' : 'Add to a report'}{' '}
      {(!enableReports || dataHubUser) && <span className='icon-padlock ml-2 paragraph-p2 d-inline-block-centered' />}
    </button>
  );

  return (
    <>
      {shouldRender && (
        <div
          className={`main-list-action-bar bg-light-blue position-absolute top-0 adjustment-of-position w-100 ${
            isVisible ? 'visible' : ''
          }`}
        >
          <div className='mx-auto main-content-wrapper flex-centered justify-content-between'>
            <div className='side-left-element pl-md-5 pl-3 d-none d-lg-block' />
            <div className='main-content flex-centered filters-general-container justify-content-between pr-xl-5 px-md-4 px-3'>
              <div className='crm-contacts-select-subnav'>
                <div className='crm-contacts-list-bar'>
                  <div className='contacts-list-bar-helper-text-wrapper'>
                    <p
                      className='contacts-list-bar-helper-text text-white nowrap-item'
                      style={{ alignItems: 'center' }}
                    >
                      <SelectedCounter value={selectedUpdates?.length} /> selected
                    </p>
                  </div>
                  <div className='contacts-list-bar-action-container'>
                    {dataHubUser ? (
                      <>
                        <SaveUpdatesToFolder onlyIconOnMobile={onlyIconOnMobile} />
                        {(inboxSection || dashboardSection) && <RateMultipleUpdatesButton />}
                        {addToReportButton}
                      </>
                    ) : (
                      <>
                        {addToReportButton}
                        <SaveUpdatesToFolder onlyIconOnMobile={onlyIconOnMobile} />
                        {(inboxSection || dashboardSection) && <RateMultipleUpdatesButton />}
                      </>
                    )}
                    {allInteractions && (
                      <button
                        className='contacts-list-bar-action-container-btn'
                        onClick={() => setIsOpenAddTagsModal(true)}
                      >
                        <span className='icon-tag' style={{ fontSize: '18px', fontWeight: 600 }} />
                        {onlyIconOnMobile ? '' : 'Add a tag'}
                      </button>
                    )}
                    {allInteractions && onlyIconOnMobile && (
                      // No room for a dropdown menu on mobile with only one item in it anyway -
                      // the delete icon it would reveal, acting directly instead.
                      <button
                        className='contacts-list-bar-action-container-btn text-danger-background'
                        onClick={() => setIsOpenDeleteModal(true)}
                      >
                        <span className='icon-delete' />
                      </button>
                    )}
                    {allInteractions && !onlyIconOnMobile && (
                      <div className='contacts-list-bar-action-main-container position-relative' ref={moreOptionsRef}>
                        <button
                          className='contacts-list-bar-action-container-btn'
                          onClick={() => setIsOpenMoreOptions((prev) => !prev)}
                        >
                          <span
                            className={`more-option-button dropdown-item-element dropdown-item-element-13 ${
                              isOpenMoreOptions ? 'open' : ''
                            }`}
                          >
                            More
                          </span>
                        </button>
                        {isOpenMoreOptions && (
                          <div className='login-navbar py-2'>
                            <button
                              className='settings-button setting-item general-button d-block text-left w-100 position-relative text-danger-background'
                              onClick={() => {
                                setIsOpenMoreOptions(false);
                                setIsOpenDeleteModal(true);
                              }}
                            >
                              <span className='icon-delete mr-3' />
                              Delete {selectedUpdates.length === 1 ? 'interaction' : 'interactions'}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {allInteractions && !onlyIconOnMobile && interactionsListPage && (
                    <div className='rounded-pill bg-main-white general-button specific-x-padding-12 specific-y-padding-3 flex-centered'>
                      <span className='icon-search light-50-text mr-2' />
                      <input
                        className='input-empty paragraph-p1'
                        placeholder='Search'
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.keyCode === 13) setSearchParam(searchValue.trim());
                        }}
                      />
                      {searchValue !== '' && (
                        <button
                          type='button'
                          className='general-button close-icon flex-shrink-0'
                          data-tooltip-content='Clear search'
                          data-tooltip-id='general-tooltip'
                          onClick={(e) => {
                            e.preventDefault();
                            setSearchValue('');
                            setSearchParam('');
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>
                <button
                  className='stakeholder-subnav-deselect-btn px-3 ml-0 ml-sm-2'
                  onClick={() => {
                    resetSelectedUpdatesState();
                    if (searchValue !== '') setSearchParam('');
                  }}
                >
                  Cancel <span className='icon-close paragraph-p5 ml-2' />
                </button>
              </div>
            </div>
          </div>
          {allInteractions && (
            <RemoveInteractionsModal
              openState={{ isOpen: isOpenDeleteModal, setIsOpen: setIsOpenDeleteModal }}
              interactions={selectedUpdates}
              onSuccess={resetSelectedUpdatesState}
            />
          )}
          {allInteractions && (
            <AddTagsToInteractionsModal
              openState={{ isOpen: isOpenAddTagsModal, setIsOpen: setIsOpenAddTagsModal }}
              interactions={selectedUpdates}
              onSuccess={resetSelectedUpdatesState}
            />
          )}
        </div>
      )}
    </>
  );
};

const useMainActionsBarFunctions = () => {
  const globalState = useContext(store);
  const { dispatch, state } = globalState;
  const { temporarySelectedUpdates, selectedUpdates, selectedUpdatesIds } = state ?? {};
  const resetSelectedUpdatesState = () => {
    dispatch({ type: 'MODIFY_SECTION', parameter: 'selectedUpdates', value: [] });
    dispatch({ type: 'MODIFY_SECTION', parameter: 'selectedUpdatesIds', value: [] });
  };
  const restoreSelectedUpdates = () => {
    if (temporarySelectedUpdates) {
      const { selectedUpdates, selectedUpdatesIds } = temporarySelectedUpdates;
      dispatch({ type: 'MODIFY_SECTION', parameter: 'selectedUpdates', value: selectedUpdates });
      dispatch({ type: 'MODIFY_SECTION', parameter: 'selectedUpdatesIds', value: selectedUpdatesIds });
      dispatch({ type: 'MODIFY_SECTION', parameter: 'temporarySelectedUpdates', value: null });
    }
  };
  const setTemporarySelectedUpdates = () => {
    if (selectedUpdates?.length && selectedUpdatesIds?.length) {
      dispatch({
        type: 'MODIFY_SECTION',
        parameter: 'temporarySelectedUpdates',
        value: { selectedUpdates, selectedUpdatesIds },
      });
    }
  };
  return { resetSelectedUpdatesState, restoreSelectedUpdates, setTemporarySelectedUpdates };
};

const SelectedCounter = ({ value }) => {
  const [display, setDisplay] = useState(value);
  const [anim, setAnim] = useState(null); // { prev, next, dir: 'inc'|'dec', key }

  useEffect(() => {
    if (value === display) return;
    const dir = value > display ? 'inc' : 'dec';
    setAnim({ prev: display, next: value, dir, key: Date.now() });
  }, [value, display]);

  const handleDone = () => {
    if (!anim) return;
    setDisplay(anim.next);
    setAnim(null);
  };

  return (
    <span className={`position-relative d-inline-block scounter ${value?.toString()?.length > 1 ? 'mr-1' : ''}`}>
      {/* idle state */}
      {!anim && <span className='scounter__static flex-centered'>{display}</span>}

      {/* animated swap */}
      {anim && (
        <>
          <span key={anim.key + '-out'} className={`flex-centered scounter__layer scounter__out scounter__${anim.dir}`}>
            {anim.prev}
          </span>
          <span
            key={anim.key + '-in'}
            className={`flex-centered scounter__layer scounter__in scounter__${anim.dir}`}
            onAnimationEnd={handleDone}
          >
            {anim.next}
          </span>
        </>
      )}
    </span>
  );
};

export { useMainActionsBarFunctions };
export default MainListActionsBar;
